/**
 * Passkey Service
 * Handles all passkey-related API calls for Beyond Identity integration
 */

import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';
import ufeApi from 'services/api/ufeApi';

const PASSKEY_BASE_URL = '/gway/v1/dotcom/auth';
const isSSR = () => typeof window === 'undefined' || (typeof Sephora !== 'undefined' && Sephora.isNodeRender);

const raisePasskeyError = message => {
    if (Sephora?.logger?.error) {
        Sephora.logger.error('[Passkey]', message);
    } else if (Sephora?.logger?.warn) {
        Sephora.logger.warn('[Passkey]', message);
    }

    throw new Error(message);
};

const getPasskeyConfig = () => {
    const config = Sephora?.configurationSettings?.passkey?.config;

    if (!config) {
        raisePasskeyError('Passkey configuration is missing.');
    }

    return config;
};

const ensureBrowser = () => {
    if (isSSR()) {
        raisePasskeyError('Passkey operations are unavailable during server-side rendering.');
    }
};

/**
 * Get access token from localStorage
 * Uses Storage utility which automatically handles JSON parsing and data extraction
 */
const getAccessToken = () => {
    try {
        return Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);
    } catch (error) {
        return null;
    }
};

/**
 * Create passkey binding
 * Calls Beyond Identity backend to create identity and get binding link
 * @param {string} email - User's email address (optional, will auto-detect from user profile)
 */
export const createPasskeyBinding = async (email = null) => {
    ensureBrowser();
    const accessToken = getAccessToken();

    if (!accessToken) {
        raisePasskeyError('No access token available. User must be logged in.');
    }

    const userEmail = email || userUtils.getProfileEmail();

    if (!userEmail) {
        raisePasskeyError('No user email found. Cannot create passkey binding.');
    }

    const data = await ufeApi.makeRequest(
        `${PASSKEY_BASE_URL}/passkeybinding`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ username: userEmail })
        },
        {
            // Avoid triggering login modal when binding fails; surface error to caller instead
            skipLoginPrompt: true
        }
    );

    if (data?.responseStatus >= 400 || data?.errors?.length || data?.errorCode) {
        const message = data?.errors?.[0]?.errorMessage || data?.errorMessage || 'Passkey binding failed';

        raisePasskeyError(message);
    }

    return data;
};

// Beyond Identity Configuration imported from shared constants

/**
 * Get the configured redirect URI
 * @returns {string} The redirect URI
 */
export const getRedirectUri = () => {
    const { redirectUri } = getPasskeyConfig();

    if (!redirectUri) {
        raisePasskeyError('Passkey redirectUri is not configured.');
    }

    return redirectUri;
};

/**
 * Generate Beyond Identity authenticate URL by fetching from BI authorize endpoint
 * Note: PKCE is disabled for this application, so we don't include code_challenge
 * @param {string} stateValue - Hex-encoded SHA-256 digest used as state
 * @returns {Promise<{authenticateUrl: string, state: string}>}
 */
export const generateAuthenticateUrl = async stateValue => {
    ensureBrowser();
    const config = getPasskeyConfig();
    const authDomain = `auth-${config.region}.beyondidentity.com`;
    const baseUrl = `https://${authDomain}/v1/tenants/${config.tenantId}/realms/${config.realmId}/applications/${config.applicationId}/authorize`;

    /* eslint-disable camelcase */
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        scope: 'openid',
        state: stateValue,
        // Request authenticate_url in JSON response (BI authorize supports include)
        include: 'authenticate_url'
    });
    /* eslint-enable camelcase */

    const authorizeUrl = `${baseUrl}?${params.toString()}`;
    const response = await fetch(authorizeUrl, { method: 'GET' });

    if (!response.ok) {
        const errorText = await response.text();

        raisePasskeyError(`Authorize endpoint failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    return {
        authenticateUrl: data.authenticate_url || authorizeUrl,
        state: stateValue
    };
};

/**
 * Parse redirect URL to extract authorization code and state
 * @param {string} redirectUrl - Redirect URL from authenticate()
 * @returns {Object} Parsed parameters {code, state, ...}
 */
export const parseRedirectUrl = redirectUrl => {
    const url = new URL(redirectUrl);
    const params = Object.fromEntries(
        Array.from(url.searchParams).map(([key, value]) => [key, value])
    );

    return params;
};

/**
 * Complete passkey login
 * Validates authentication result with backend and creates session
 * @param {Object} loginParams - Login parameters
 * @param {string} loginParams.code - Authorization code from BI
 * @param {string} loginParams.state - State parameter for CSRF protection (hex-encoded SHA-256)
 * @param {string} loginParams.redirectUrl - Base redirect URI (e.g., 'sephoraapp://callback')
 * @param {string} loginParams.identityId - Beyond Identity identity ID
 * @param {string} loginParams.passkeyId - Beyond Identity credential ID
 * @param {string} loginParams.randomNumber - Hashed random number (SHA-256 hex-encoded, 64 chars)
 * @param {string} loginParams.codeVerifier - PKCE code verifier (optional)
 * @param {string} loginParams.email - User email
 * @returns {Promise<Object>} Session data with tokens
 */
export const passkeyLogin = async loginParams => {
    ensureBrowser();
    const {
        code, state, redirectUrl, identityId, passkeyId, randomNumber, codeVerifier, email
    } = loginParams;

    const queryParams = new URLSearchParams({
        code,
        state,
        redirectUrl,
        identityId,
        passkeyId
    });

    if (codeVerifier) {
        queryParams.append('codeVerifier', codeVerifier);
    }

    const requestBody = {
        email,
        randomNumber
    };

    const data = await ufeApi.makeRequest(`${PASSKEY_BASE_URL}/passkeylogin?${queryParams.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    });

    if (data?.responseStatus >= 400 || data?.errors?.length || data?.errorCode) {
        const message = data?.errors?.[0]?.errorMessage || data?.errorMessage || 'Passkey login failed';

        raisePasskeyError(message);
    }

    return data;
};

/**
 * Get account passkeys (account settings)
 * @returns {Promise<Object>} Passkeys payload from backend
 */
export const getPasskeys = async () => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeys`;

    const data = await ufeApi.makeRequest(
        url,
        { method: 'GET' },
        {
            // Do not surface sign-in modal on passkey fetch failures; caller will handle
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to fetch passkeys');
    }

    return data;
};

/**
 * Remove an account passkey (account settings)
 * @param {string} passkeyId
 * @param {{firstName?: string, lastName?: string}} userData
 * @returns {Promise<Object>} Backend response
 */
export const removePasskey = async (passkeyId, userData) => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeys/${passkeyId}?firstName=${userData?.firstName}&lastName=${userData?.lastName}`;

    const data = await ufeApi.makeRequest(
        url,
        { method: 'DELETE' },
        {
            // Avoid triggering sign-in modal for passkey delete failures
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to remove passkey');
    }

    return data;
};

/**
 * Create passkey metadata (account + device context)
 * @param {Object} input - Metadata payload
 * @param {string} input.passkeyId - Beyond Identity passkey/credential ID
 * @param {string} input.identityId - Beyond Identity identity ID
 * @param {string} input.deviceName - Friendly device/browser name
 * @param {string} input.email - User email
 * @param {string} [input.firstName] - User first name
 * @param {string} [input.lastName] - User last name
 * @returns {Promise<Object>} Backend response
 */
export const createPasskeyMetaData = async input => {
    ensureBrowser();
    const url = `${PASSKEY_BASE_URL}/passkeymetadata`;

    const data = await ufeApi.makeRequest(
        url,
        {
            method: 'POST',
            body: JSON.stringify(input),
            headers: {
                'Content-Type': 'application/json'
            }
        },
        {
            // Metadata errors should not prompt re-login
            skipLoginPrompt: true
        }
    );

    if (data && data.errorCode) {
        raisePasskeyError(data.errorMessage || 'Failed to create passkey metadata');
    }

    return data;
};

export default {
    createPasskeyBinding,
    generateAuthenticateUrl,
    parseRedirectUrl,
    passkeyLogin,
    getRedirectUri,
    getPasskeys,
    removePasskey,
    createPasskeyMetaData
};
